import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ---------- Sentry (graceful no-op when REACT_APP_SENTRY_DSN empty) ----------
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV || "development",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });
}

// ---------- Plausible Analytics (graceful no-op when REACT_APP_PLAUSIBLE_DOMAIN empty) ----------
const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
if (PLAUSIBLE_DOMAIN && !document.querySelector('script[data-plausible-loaded]')) {
  const s = document.createElement("script");
  s.defer = true;
  s.setAttribute("data-domain", PLAUSIBLE_DOMAIN);
  s.setAttribute("data-plausible-loaded", "true");
  // script.js already tracks pushState navigations for SPAs like react-router.
  s.src = "https://plausible.io/js/script.js";
  document.head.appendChild(s);
  window.plausible = window.plausible || function () {
    (window.plausible.q = window.plausible.q || []).push(arguments);
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const FallbackUI = () => (
  <div className="min-h-screen flex items-center justify-center p-8 bg-ccc-ivory font-serif text-ccc-plum">
    <div className="max-w-md text-center">
      <h1 className="text-3xl mb-2">Something went wrong.</h1>
      <p className="text-stone-700 mb-4">Our team has been notified. Please refresh the page.</p>
      <button onClick={() => window.location.reload()} className="bg-ccc-purple text-white px-5 py-2 uppercase text-sm tracking-wider">
        Reload
      </button>
    </div>
  </div>
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<FallbackUI />}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
